import { __decorate } from "tslib";
import { RelationshipKind, } from '@metaos/mfs-client';
import { CardNotFound } from '../errors/errorClasses/cardNotFound';
import { UPDATED_AT_PROPERTY_NAME } from '../utility/cardPropertyUtils';
import { getDistinctParentCardIds, toCard } from '../utility/cardUtils';
import { MFS_CARD_TYPE, MFS_TAG_CONTAINER_TYPE, MFS_TAG_TYPE, TAG_CONTAINER_ID } from '../utility/constant/constants';
import { containsRelationship, tagContainerRelationship, taggedRelationship, } from '../utility/constant/internalRelationships';
import { toTag } from '../utility/taggingUtils';
import { logVerbose, logger } from '../utility/telemetry';
import { IsMfsTagTypeguard, getMfsCardTypeguard } from '../validation/typeGuard/folderInternalTypeGuard';
import { FoldersEventEmitter } from './eventEmitter';
/** @inheritdoc */
export class EventClient {
    constructor(datastore, cardClient) {
        this.datastore = datastore;
        this.cardClient = cardClient;
        this.emitterRegistered = false;
        this.collectionChangedCallback = (changeEvent) => {
            try {
                if (changeEvent.changeKind === 'add') {
                    if (changeEvent.label === MFS_CARD_TYPE) {
                        const [, card] = this.getMfsCardAndCardOrThrow(changeEvent.collectionId);
                        const cardCreatedEvent = {
                            card: card,
                        };
                        this.eventEmitter.emitUnscopedFolderEvent(cardCreatedEvent, 'cardCreated');
                    }
                }
                else if (changeEvent.changeKind === 'addElement') {
                    this.fireIfTagContainer(changeEvent, 'tagCreated');
                }
                else if (changeEvent.changeKind === 'removeElement' || changeEvent.changeKind === 'clear') {
                    this.fireIfTagContainer(changeEvent, 'tagDeleted');
                }
            }
            catch (error) {
                return; // Swallow all errors in event handling.
            }
        };
        /**
         * Convert MFS property change events to Folder item change events and emit to the consumers.
         * @param changeEvent Event that was emitted from MFS.
         */
        this.propertyChangedCallback = (changeEvent) => {
            try {
                switch (changeEvent.label) {
                    case MFS_TAG_TYPE: {
                        const [, tag] = this.getMfsTagAndTagOrThrow(changeEvent.itemId);
                        const tagPatchedEvent = {
                            tag: tag,
                        };
                        this.eventEmitter.emitUnscopedFolderEvent(tagPatchedEvent, 'tagPatched');
                        break;
                    }
                    case MFS_CARD_TYPE: {
                        if (this.isUpdatedAtTimePropertyChange(changeEvent))
                            break;
                        const [, card] = this.getMfsCardAndCardOrThrow(changeEvent.itemId);
                        const cardChangeEvent = {
                            patchedCard: card,
                        };
                        this.emitToAllParents([changeEvent.itemId], cardChangeEvent, 'cardPatched');
                    }
                }
            }
            catch (error) {
                return; // Swallow all errors in event handling.
            }
        };
        /**
         * Convert MFS relationship change events to Folder Activity change events and emit to the consumers.
         * @param changeEvent Event that was emitted from MFS.
         */
        this.relationshipChangedCallback = (changeEvent) => {
            try {
                // TwoWay RelationshipChange events get fired for each end. Only care about the 'out' direction.
                if (changeEvent.relationship.kind === RelationshipKind.TwoWay && changeEvent.relationship.direction === 'in') {
                    return;
                }
                switch (changeEvent.relationship.type) {
                    case tagContainerRelationship.type: {
                        if (changeEvent.changeKind === 'add' || changeEvent.changeKind === 'update') {
                            this.emitTagCreatedEvent(changeEvent);
                        }
                        else if (changeEvent.changeKind === 'remove') {
                            this.emitTagDeletedEvent(changeEvent);
                        }
                        return;
                    }
                    case taggedRelationship.type: {
                        if (changeEvent.changeKind === 'add' || changeEvent.changeKind === 'update') {
                            this.emitTaggedEvent(changeEvent);
                        }
                        else if (changeEvent.changeKind === 'remove') {
                            this.emitUntaggedEvent(changeEvent);
                        }
                        return;
                    }
                    case containsRelationship.type: {
                        if (changeEvent.changeKind === 'add' || changeEvent.changeKind === 'update') {
                            this.emitCardAddedEvent(changeEvent);
                        }
                        else if (changeEvent.changeKind === 'remove') {
                            this.emitCardRemovedEvent(changeEvent);
                        }
                        return;
                    }
                }
            }
            catch (error) {
                return; // Swallow all errors in event handling.
            }
        };
        /**
         * Emit stale data detected event to consumers.
         */
        this.staleDataDetectedCallback = () => {
            this.eventEmitter.emitUnscopedFolderEvent(null, 'staleDataDetected');
        };
        this.eventEmitter = new FoldersEventEmitter();
    }
    /** @inheritdoc */
    onScopedEvent(cardId, scopedEventType, callback) {
        this.registerMfsEvents();
        const cb = (arg) => this.callbackWrapper(cardId, arg, callback);
        this.eventEmitter.addListener(scopedEventType, cb);
        return () => {
            logger.logVerbose('onScopedEvent', 'Removing listener for event', { eventType: scopedEventType });
            this.eventEmitter.removeListener(scopedEventType, cb);
        };
    }
    /** @inheritdoc */
    onUnscopedEvent(event, callback) {
        this.registerMfsEvents();
        this.eventEmitter.addListener(event, callback);
        return () => {
            logger.logVerbose('onUnscopedEvent', 'Removing listener for event', { eventType: event });
            this.eventEmitter.removeListener(event, callback);
        };
    }
    /** A filter to only emit if the requested Card was affected. */
    callbackWrapper(cardId, arg, callback) {
        if ('subscribedUniqueId' in arg && arg['subscribedUniqueId'] === cardId) {
            logger.logVerbose('callbackWrapper', 'Emitting event', { eventType: typeof arg });
            callback(arg);
        }
    }
    /** Determine if the event type has any active subscriptions. */
    isSubscribedTo(eventName) {
        return this.eventEmitter.listenerCount(eventName) > 0;
    }
    /**
     * Attach Folders callbacks to mfs event listeners.
     */
    registerMfsEvents() {
        if (this.emitterRegistered === true) {
            logger.logVerbose('registerMfsEvents', 'Mfs event emitter already registered');
            return;
        }
        logger.logVerbose('registerMfsEvents', 'Registering Mfs events');
        //this.datastore.on('itemsChanged', this.itemsChangedCallback); // TODO: don't think we need this actually since items only exist inside of folders and can't be created separately?
        this.datastore.on('propertyChanged', this.propertyChangedCallback);
        this.datastore.on('collectionChanged', this.collectionChangedCallback);
        this.datastore.on('relationshipChanged', this.relationshipChangedCallback);
        this.datastore.on('staleDataDetected', this.staleDataDetectedCallback);
        this.emitterRegistered = true;
    }
    /** Fires the given event if it is the Tag container */
    fireIfTagContainer(changeEvent, eventType) {
        if (changeEvent.label === MFS_TAG_CONTAINER_TYPE) {
            this.getMfsItemOrThrow(changeEvent.collectionId, (item) => item.label === MFS_TAG_CONTAINER_TYPE && item.labelId === TAG_CONTAINER_ID);
            if ('elements' in changeEvent) {
                for (const item of changeEvent.elements) {
                    const [, tag] = this.getMfsTagAndTagOrThrow(item.itemId);
                    const tagCreatedEvent = {
                        tag: tag,
                    };
                    this.eventEmitter.emitUnscopedFolderEvent(tagCreatedEvent, eventType);
                }
            }
        }
    }
    /** Build and emit a tagCreated event from an MfsRelationshipChange event */
    emitTagCreatedEvent(changeEvent) {
        const eventName = 'tagCreated';
        if (!this.isSubscribedTo(eventName))
            return;
        const [, tag] = this.getMfsTagAndTagOrThrow(changeEvent.relatedItemId);
        const tagCreatedEvent = {
            tag: tag,
        };
        this.eventEmitter.emitUnscopedFolderEvent(tagCreatedEvent, eventName);
    }
    /** Build and emit a tagCreated event from an MfsRelationshipChange event */
    emitTagDeletedEvent(changeEvent) {
        const eventName = 'tagDeleted';
        if (!this.isSubscribedTo(eventName))
            return;
        const [, tag] = this.getMfsTagAndTagOrThrow(changeEvent.relatedItemId);
        const tagCreatedEvent = {
            tag: tag,
        };
        this.eventEmitter.emitUnscopedFolderEvent(tagCreatedEvent, eventName);
    }
    /** Build and emit a tagged event from an MfsRelationshipChange event */
    emitTaggedEvent(changeEvent) {
        const eventName = 'tagged';
        if (!this.isSubscribedTo(eventName))
            return;
        const [mfsCard, card] = this.getMfsCardAndCardOrThrow(changeEvent.itemId);
        const [, tag] = this.getMfsTagAndTagOrThrow(changeEvent.relatedItemId);
        const taggedEvent = {
            tag: tag,
            taggedCard: card,
        };
        this.eventEmitter.emitScopedFolderEvent(card.uniqueId, taggedEvent, eventName);
        this.emitToAllParents([mfsCard.id], taggedEvent, eventName);
    }
    /** Build and emit an untagged event from an MfsRelationshipChange event */
    emitUntaggedEvent(changeEvent) {
        const eventName = 'untagged';
        if (!this.isSubscribedTo(eventName))
            return;
        const mfsCardAndCard = this.getMfsCardAndCardOrThrow(changeEvent.itemId);
        const mfsTagAndTag = this.getMfsTagAndTagOrThrow(changeEvent.relatedItemId);
        const untaggedEvent = {
            tag: mfsTagAndTag[1],
            untaggedCard: mfsCardAndCard[1],
        };
        this.eventEmitter.emitScopedFolderEvent(mfsCardAndCard[1].uniqueId, untaggedEvent, eventName);
        this.emitToAllParents([mfsCardAndCard[0].id], untaggedEvent, eventName);
    }
    /** Build and emit a cardAdded event from an MfsRelationshipChange event */
    emitCardAddedEvent(changeEvent) {
        const eventName = 'cardAdded';
        if (!this.isSubscribedTo(eventName))
            return;
        const [parentMfsCard, parentCard] = this.getMfsCardAndCardOrThrow(changeEvent.itemId);
        const [, childCard] = this.getMfsCardAndCardOrThrow(changeEvent.relatedItemId);
        const cardAddedEvent = {
            addedCard: childCard,
            parentCard: parentCard,
        };
        // Emit to self, affected parent folder, and parent folders of that parent folder. Don't emit to other parents of self.
        this.eventEmitter.emitScopedFolderEvent(childCard.uniqueId, cardAddedEvent, eventName);
        this.eventEmitter.emitScopedFolderEvent(parentCard.uniqueId, cardAddedEvent, eventName);
        this.emitToAllParents([parentMfsCard.id], cardAddedEvent, eventName);
    }
    /** Build and emit a cardRemoved event from an MfsRelationshipChange event */
    emitCardRemovedEvent(changeEvent) {
        const eventName = 'cardRemoved';
        if (!this.isSubscribedTo(eventName))
            return;
        const [parentMfsCard, parentCard] = this.getMfsCardAndCardOrThrow(changeEvent.itemId);
        const [, childCard] = this.getMfsCardAndCardOrThrow(changeEvent.relatedItemId);
        const cardChangedEvent = {
            removedCard: childCard,
            parentCard: parentCard,
        };
        // Emit to self, affected parent folder, and parent folders of that parent folder. Don't emit to other parents of self.
        this.eventEmitter.emitScopedFolderEvent(childCard.uniqueId, cardChangedEvent, eventName);
        this.eventEmitter.emitScopedFolderEvent(parentCard.uniqueId, cardChangedEvent, eventName);
        this.emitToAllParents([parentMfsCard.id], cardChangedEvent, eventName);
    }
    /** Get and return both the MfsCard and Card. Throw if any do not exist. */
    getMfsCardAndCardOrThrow(mfsId) {
        const mfsCard = this.getMfsItemOrThrow(mfsId, getMfsCardTypeguard());
        const card = toCard(this.datastore, mfsCard);
        return [mfsCard, card];
    }
    /** Get and return both the MfsTag and Tag. Throw if any do not exist. */
    getMfsTagAndTagOrThrow(mfsId) {
        const mfsTag = this.getMfsItemOrThrow(mfsId, IsMfsTagTypeguard);
        const tag = toTag(mfsTag);
        return [mfsTag, tag];
    }
    /** Get MfsItem or throw an error. */
    getMfsItemOrThrow(mfsId, typeguard) {
        const mfsItem = this.datastore.getItem(mfsId, typeguard);
        if (!mfsItem)
            throw new CardNotFound(`CardId ${mfsId} not found`);
        logger.logVerbose('getMfsItemOrThrow', 'Retrieved MfsItem', { itemId: mfsItem.id });
        return mfsItem;
    }
    /** Emits the change event to all parents of the specified card that changed. */
    emitToAllParents(changedMfsItemIds, eventMinusUniqueId, eventName) {
        logger.logVerbose('emitToAllParents', 'Emitting event to all parents of affected mfsItems.', {
            eventType: eventName,
            affectedItemIds: changedMfsItemIds,
        });
        for (const parentId of getDistinctParentCardIds(this.datastore, changedMfsItemIds)) {
            this.eventEmitter.emitScopedFolderEvent(parentId, eventMinusUniqueId, eventName);
        }
        logger.logVerbose('emitToAllParents', 'Finished emitting event to all parents of affected mfsItems.', {
            eventType: eventName,
            affectedItemIds: changedMfsItemIds,
        });
    }
    /** Check if the `MfsPropertyChange` event is because of `updatedAt` timestamp update. */
    isUpdatedAtTimePropertyChange(event) {
        const touchedAtTimeUniqueKey = this.datastore.getMfsItemKey(UPDATED_AT_PROPERTY_NAME);
        return touchedAtTimeUniqueKey in event.changedProperties;
    }
}
__decorate([
    logVerbose()
], EventClient.prototype, "emitTagCreatedEvent", null);
__decorate([
    logVerbose()
], EventClient.prototype, "emitTagDeletedEvent", null);
__decorate([
    logVerbose()
], EventClient.prototype, "emitTaggedEvent", null);
__decorate([
    logVerbose()
], EventClient.prototype, "emitUntaggedEvent", null);
__decorate([
    logVerbose()
], EventClient.prototype, "emitCardAddedEvent", null);
__decorate([
    logVerbose()
], EventClient.prototype, "emitCardRemovedEvent", null);
//# sourceMappingURL=eventClient.js.map